import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition, RadioGroup } from "@headlessui/react";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { isSystemListId } from "../../utils/myVendorListsConstants";
import { extractVendorListMembers } from "../../utils/myVendorListsUtils";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function listMemberCount(list) {
  const members = extractVendorListMembers(list);
  return members.length || list.vendorCount || 0;
}

export default function AddToListModal({
  isOpen,
  onClose,
  selectedVendors = [],
  vendorLists = [],
  onAddToList,
  isSubmitting = false,
}) {
  const [selectedList, setSelectedList] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [addedToListName, setAddedToListName] = useState("");

  const orderedSystemLists = useMemo(
    () => vendorLists.filter((list) => isSystemListId(list.id)),
    [vendorLists]
  );
  const orderedUserLists = useMemo(
    () => vendorLists.filter((list) => !isSystemListId(list.id)),
    [vendorLists]
  );

  useEffect(() => {
    if (!isOpen) {
      setShowSuccess(false);
      setAddedToListName("");
      setSelectedList(null);
    }
  }, [isOpen]);

  const handleAddToList = async () => {
    if (!selectedList || selectedVendors.length === 0 || isSubmitting) return;
    try {
      await onAddToList?.(selectedList, selectedVendors);
      setAddedToListName(selectedList.name);
      setShowSuccess(true);
      setSelectedList(null);
    } catch {
      // Parent surfaces error feedback; keep modal open for retry.
    }
  };

  const handleClose = () => {
    setSelectedList(null);
    setShowSuccess(false);
    setAddedToListName("");
    onClose?.();
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-3 w-full sm:mt-0 sm:text-left">
                  {showSuccess ? (
                    <>
                      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                        <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true" />
                      </div>
                      <Dialog.Title className="text-center text-base font-semibold text-gray-900">
                        Vendors Added Successfully!
                      </Dialog.Title>
                      <p className="mt-4 text-center text-sm text-gray-500">
                        {selectedVendors.length} vendor
                        {selectedVendors.length === 1 ? "" : "s"} added to &ldquo;{addedToListName}
                        &rdquo;.
                      </p>
                      <div className="mt-5 flex justify-center gap-3">
                        <button
                          type="button"
                          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
                          onClick={handleClose}
                        >
                          Done
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <Dialog.Title className="text-base font-semibold text-gray-900">
                        Add to Vendor List
                      </Dialog.Title>
                      <p className="mt-2 text-sm text-gray-500">
                        Select a vendor list to add {selectedVendors.length}{" "}
                        {selectedVendors.length === 1 ? "vendor" : "vendors"} to.
                      </p>

                      <div className="mt-4">
                        {vendorLists.length === 0 ? (
                          <div className="py-8 text-center">
                            <p className="text-sm font-medium text-gray-900">No vendor lists</p>
                            <p className="mt-1 text-sm text-gray-500">
                              Create a list first, then add vendors to it.
                            </p>
                          </div>
                        ) : (
                          <RadioGroup value={selectedList} onChange={setSelectedList}>
                            <RadioGroup.Label className="sr-only">
                              Select a vendor list
                            </RadioGroup.Label>
                            <div className="max-h-64 space-y-2 overflow-y-auto">
                              {orderedSystemLists.map((list) => (
                                <RadioGroup.Option
                                  key={list.id}
                                  value={list}
                                  className={({ active, checked }) =>
                                    classNames(
                                      checked
                                        ? "border-indigo-600 ring-2 ring-indigo-600"
                                        : "border-gray-300",
                                      active ? "border-indigo-600 ring-2 ring-indigo-600" : "",
                                      "relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
                                    )
                                  }
                                >
                                  {({ checked }) => (
                                    <>
                                      <span className="flex flex-1">
                                        <span className="flex flex-col">
                                          <span className="flex items-center gap-2">
                                            <RadioGroup.Label
                                              as="span"
                                              className="block text-sm font-medium text-gray-900"
                                            >
                                              {list.name}
                                            </RadioGroup.Label>
                                            <span className="inline-flex items-center rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-500 ring-1 ring-inset ring-gray-200">
                                              Default
                                            </span>
                                          </span>
                                          <RadioGroup.Description
                                            as="span"
                                            className="mt-1 text-sm text-gray-500"
                                          >
                                            {listMemberCount(list)}{" "}
                                            {listMemberCount(list) === 1 ? "vendor" : "vendors"}
                                          </RadioGroup.Description>
                                        </span>
                                      </span>
                                      <CheckCircleIcon
                                        className={classNames(
                                          !checked ? "invisible" : "",
                                          "h-5 w-5 text-indigo-600"
                                        )}
                                        aria-hidden="true"
                                      />
                                    </>
                                  )}
                                </RadioGroup.Option>
                              ))}

                              {orderedSystemLists.length > 0 && orderedUserLists.length > 0 ? (
                                <div className="relative my-3">
                                  <div
                                    className="absolute inset-0 flex items-center"
                                    aria-hidden="true"
                                  >
                                    <div className="w-full border-t border-gray-200" />
                                  </div>
                                  <div className="relative flex justify-start">
                                    <span className="bg-white pr-2 text-xs font-medium uppercase tracking-wide text-gray-400">
                                      Your lists
                                    </span>
                                  </div>
                                </div>
                              ) : null}

                              {orderedUserLists.map((list) => (
                                <RadioGroup.Option
                                  key={list.id}
                                  value={list}
                                  className={({ active, checked }) =>
                                    classNames(
                                      checked
                                        ? "border-indigo-600 ring-2 ring-indigo-600"
                                        : "border-gray-300",
                                      active ? "border-indigo-600 ring-2 ring-indigo-600" : "",
                                      "relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none"
                                    )
                                  }
                                >
                                  {({ checked }) => (
                                    <>
                                      <span className="flex flex-1">
                                        <span className="flex flex-col">
                                          <RadioGroup.Label
                                            as="span"
                                            className="block text-sm font-medium text-gray-900"
                                          >
                                            {list.name}
                                          </RadioGroup.Label>
                                          <RadioGroup.Description
                                            as="span"
                                            className="mt-1 text-sm text-gray-500"
                                          >
                                            {listMemberCount(list)}{" "}
                                            {listMemberCount(list) === 1 ? "vendor" : "vendors"}
                                          </RadioGroup.Description>
                                        </span>
                                      </span>
                                      <CheckCircleIcon
                                        className={classNames(
                                          !checked ? "invisible" : "",
                                          "h-5 w-5 text-indigo-600"
                                        )}
                                        aria-hidden="true"
                                      />
                                    </>
                                  )}
                                </RadioGroup.Option>
                              ))}
                            </div>
                          </RadioGroup>
                        )}
                      </div>

                      <div className="mt-5 flex justify-end gap-3">
                        <button
                          type="button"
                          className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          onClick={handleClose}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                          onClick={handleAddToList}
                          disabled={!selectedList || selectedVendors.length === 0 || isSubmitting}
                        >
                          {isSubmitting ? "Adding…" : "Add to List"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
